import React, { useEffect, useCallback, useRef, useState, lazy, Suspense } from "react";
import {
  Box,
  IconButton,
  Drawer,
  Backdrop,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { MenuRounded } from "@mui/icons-material";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../shared/hooks/sharedHooks";
import {
  setSidebarState,
  toggleSidebar,
  closeArtifactPanel,
} from "../midasPageSlice";
import { startNewChat } from "../../../shared/chatBot/chatBotSlice";
import { setActiveThreadId } from "../midasPageSlice";
import useTranslation from "../../../shared/hooks/useTranslation";
import useMidasUrlMachineContext from "../hooks/useMidasUrlMachineContext";
import MidasSidebar from "./MidasSidebar";
import MidasChatArea from "./MidasChatArea";
import MidasDragHandle from "./MidasDragHandle";
import MidasPhoneArtifactSheet from "./MidasPhoneArtifactSheet";
import { dt } from "../../../shared/themes/redesignTokens";

// Lazy-load the artifact panel (F6.10)
const MidasArtifactPanel = lazy(() => import("./MidasArtifactPanel"));

// ===================================================================
//  MIDAS Design Tokens (local subset)
// ===================================================================

// Dark tokens removed — sidebar drawers now use dt light tokens

// ===================================================================
//  F6.4 + F6.7: Keyframes for MIDAS polish animations
//  Injected once — includes @media prefers-reduced-motion override
// ===================================================================

const MIDAS_POLISH_STYLE_ID = "midas-polish-keyframes";
const MIDAS_POLISH_KEYFRAMES = `
  @keyframes midasCardReveal {
    0% { opacity: 0; transform: translateY(10px) scale(0.98); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes midasSlideInRight {
    0% { opacity: 0; transform: translateX(20px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  @keyframes midasSlideUpSheet {
    0% { opacity: 0; transform: translateY(30px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes midasFadeLeft {
    0% { opacity: 0; transform: translateX(-12px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-delay: 0ms !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

// ===================================================================
//  MidasPage -- Full-screen 3-panel layout (+ artifact panel)
//  F6.1: Tablet layout with overlay sidebar + overlay artifact
//  F6.2: Phone layout with full-screen drawer sidebar
//  F6.4: Panel transition animations
//  F6.6: Keyboard shortcuts (Ctrl+N, Ctrl+K, Esc)
//  F6.7: prefers-reduced-motion support
//  F6.8: ARIA labels, focus management, screen reader announcements
// ===================================================================

/** Ref exposed for Ctrl+K shortcut to focus sidebar search */
export const sidebarSearchRef = { focus: () => {} };

const MidasPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const sidebarState = useAppSelector(
    (state) => state.midasPage.sidebarState
  );
  const artifactPanelOpen = useAppSelector(
    (state) => state.midasPage.artifactPanelOpen
  );

  // M7-B2: Read machineId from URL params (QR kiosk integration)
  useMidasUrlMachineContext();

  // F6.7: Reduced motion preference
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  // F6.8: Screen reader live announcements for panel state changes
  const [announceText, setAnnounceText] = useState("");

  // F6.8: Focus management — save trigger ref to return focus on panel close
  const phoneMenuBtnRef = useRef<HTMLButtonElement>(null);
  const prevArtifactOpen = useRef(artifactPanelOpen);
  const prevSidebarState = useRef(sidebarState);

  // F6.4: Inject MIDAS polish keyframes once
  useEffect(() => {
    if (!document.getElementById(MIDAS_POLISH_STYLE_ID)) {
      const style = document.createElement("style");
      style.id = MIDAS_POLISH_STYLE_ID;
      style.textContent = MIDAS_POLISH_KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);

  // F6.8: Announce artifact panel state changes to screen readers
  useEffect(() => {
    if (prevArtifactOpen.current !== artifactPanelOpen) {
      setAnnounceText(
        artifactPanelOpen
          ? translate("midas.aria.artifactPanelOpened") || "Artifact panel opened"
          : translate("midas.aria.artifactPanelClosed") || "Artifact panel closed"
      );
      prevArtifactOpen.current = artifactPanelOpen;
    }
  }, [artifactPanelOpen, translate]);

  // F6.8: Announce sidebar state changes to screen readers
  useEffect(() => {
    if (prevSidebarState.current !== sidebarState) {
      const key =
        sidebarState === "expanded"
          ? "midas.aria.sidebarExpanded"
          : sidebarState === "collapsed"
            ? "midas.aria.sidebarCollapsed"
            : "midas.aria.sidebarHidden";
      setAnnounceText(translate(key) || sidebarState);
      prevSidebarState.current = sidebarState;
    }
  }, [sidebarState, translate]);

  // F6.8: Return focus to phone menu button when sidebar drawer closes
  useEffect(() => {
    if (sidebarState === "hidden" && phoneMenuBtnRef.current) {
      phoneMenuBtnRef.current.focus();
    }
  }, [sidebarState]);

  const isDesktop = useMediaQuery("(min-width:1201px)");
  const isTablet = useMediaQuery(
    "(min-width:769px) and (max-width:1200px)"
  );
  const isPhone = useMediaQuery("(max-width:768px)");

  // Responsive sidebar defaults
  useEffect(() => {
    if (isPhone) {
      dispatch(setSidebarState("hidden"));
    } else if (isTablet) {
      dispatch(setSidebarState("collapsed"));
    } else {
      dispatch(setSidebarState("expanded"));
    }
  }, [isDesktop, isTablet, isPhone, dispatch]);

  const handleToggleSidebar = useCallback(() => {
    if (isPhone) {
      // Phone: toggle between hidden and expanded (drawer overlay)
      dispatch(
        setSidebarState(sidebarState === "hidden" ? "expanded" : "hidden")
      );
    } else if (isTablet) {
      // Tablet: toggle between collapsed (icon rail) and expanded (overlay)
      dispatch(
        setSidebarState(
          sidebarState === "expanded" ? "collapsed" : "expanded"
        )
      );
    } else {
      dispatch(toggleSidebar());
    }
  }, [isPhone, isTablet, sidebarState, dispatch]);

  const handleCloseMobileDrawer = useCallback(() => {
    if (isTablet) {
      dispatch(setSidebarState("collapsed"));
    } else {
      dispatch(setSidebarState("hidden"));
    }
  }, [isTablet, dispatch]);

  // ── F6.6: Keyboard shortcuts ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+N / Cmd+N: New chat
      if ((e.ctrlKey || e.metaKey) && e.key === "n") {
        e.preventDefault();
        dispatch(startNewChat());
        dispatch(setActiveThreadId(null));
        return;
      }
      // Ctrl+K / Cmd+K: Focus conversation search
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        // If sidebar is collapsed/hidden, expand it first
        if (sidebarState !== "expanded") {
          dispatch(setSidebarState("expanded"));
        }
        // Focus the search input (if sidebar search is wired)
        setTimeout(() => sidebarSearchRef.focus(), 100);
        return;
      }
      // Esc: Close open panels (artifact first, then sidebar overlay)
      if (e.key === "Escape") {
        if (artifactPanelOpen) {
          dispatch(closeArtifactPanel());
          return;
        }
        // On tablet/phone, close sidebar overlay
        if (
          (isTablet || isPhone) &&
          sidebarState === "expanded"
        ) {
          handleCloseMobileDrawer();
          return;
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    dispatch,
    sidebarState,
    artifactPanelOpen,
    isTablet,
    isPhone,
    handleCloseMobileDrawer,
  ]);

  // Artifact panel lazy fallback
  const artifactFallback = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        minWidth: "360px",
      }}
    >
      <CircularProgress size={24} sx={{ color: dt.textMuted }} />
    </Box>
  );

  // ── Tablet: sidebar expanded = overlay, collapsed = inline icon rail ──
  const tabletSidebarExpanded = isTablet && sidebarState === "expanded";

  // F6.4: Backdrop transition — disabled when reduced motion preferred
  const backdropTransition = prefersReducedMotion ? "none" : "opacity 0.25s ease";

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        overflow: "hidden",
        backgroundColor: dt.pageBg,
        position: "relative",
      }}
    >
      {/* F6.8: Screen reader live announcements */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
        }}
      >
        {announceText}
      </Box>

      {/* ── Desktop: inline sidebar ── */}
      {isDesktop && (
        <MidasSidebar
          sidebarState={sidebarState}
          onToggle={handleToggleSidebar}
          prefersReducedMotion={prefersReducedMotion}
        />
      )}

      {/* ── Tablet: icon rail (always) + overlay sidebar when expanded ── */}
      {isTablet && (
        <>
          {/* Inline icon rail — always visible */}
          <MidasSidebar
            sidebarState="collapsed"
            onToggle={handleToggleSidebar}
            prefersReducedMotion={prefersReducedMotion}
          />

          {/* Overlay sidebar on tablet when expanded */}
          <Drawer
            anchor="left"
            open={tabletSidebarExpanded}
            onClose={handleCloseMobileDrawer}
            transitionDuration={prefersReducedMotion ? 0 : 250}
            sx={{
              "& .MuiDrawer-paper": {
                width: "280px",
                left: "56px", // offset past the icon rail
                backgroundColor: dt.cardBg,
                border: "none",
              },
              "& .MuiBackdrop-root": {
                backgroundColor: "rgba(0,0,0,0.3)",
                left: "56px", // backdrop starts after icon rail
                transition: backdropTransition,
              },
            }}
          >
            <MidasSidebar
              sidebarState="expanded"
              onToggle={handleCloseMobileDrawer}
              prefersReducedMotion={prefersReducedMotion}
            />
          </Drawer>
        </>
      )}

      {/* ── Phone: hamburger button + full-screen drawer sidebar ── */}
      {isPhone && (
        <>
          {/* Hamburger button overlaid on chat area.
              BUG-MIDAS-MOBILE-0414: Button was visible but unclickable on phone.
              Root cause diagnosis: three reinforcing issues.
                1. zIndex was 10 — the outer MidasPage <Box> has `position: relative`
                   (line ~264) which creates a positioning context, and the sibling
                   chat area <Box> (line ~395) has `overflow: hidden`. Later-painted
                   opaque children (MidasChatHeader has `backgroundColor: dt.cardBg`)
                   sit above a low z-index in source order on some Android Chrome
                   builds that treat `overflow:hidden` as a stacking boundary.
                2. Tap target was ~32x32 (padding 6px + icon 20px) — below the
                   44x44 WCAG / Samsung One UI minimum. Android Chrome can reject
                   accidental edge taps in that range.
                3. `pointerEvents` was not explicit — any ancestor defaulting to
                   `pointerEvents: none` during transition animations would
                   swallow taps.
              Fix: raise zIndex above MUI AppBar (1250) and MidasChatHeader but
              below Drawer (1200) and the floating Snackbar stack. Use 1300 to
              guarantee clicks reach the button. Increase padding to 10px for a
              44x44 tap target. Set `pointerEvents: "auto"` explicitly. */}
          {sidebarState === "hidden" && (
            <IconButton
              ref={phoneMenuBtnRef}
              onClick={handleToggleSidebar}
              size="small"
              aria-label={translate("midas.aria.phoneMenuButton") || "Open sidebar menu"}
              sx={{
                position: "absolute",
                top: "8px",
                left: "8px",
                zIndex: 1300,
                pointerEvents: "auto",
                color: dt.textSecondary,
                backgroundColor: dt.cardBg,
                border: `1px solid ${dt.cardBorder}`,
                borderRadius: "8px",
                padding: "10px",
                minWidth: "44px",
                minHeight: "44px",
                boxShadow: dt.cardShadow,
                "&:hover": {
                  backgroundColor: dt.rowHover,
                },
              }}
            >
              <MenuRounded sx={{ fontSize: "20px" }} />
            </IconButton>
          )}

          {/* Mobile drawer overlay */}
          <Drawer
            anchor="left"
            open={sidebarState === "expanded"}
            onClose={handleCloseMobileDrawer}
            transitionDuration={prefersReducedMotion ? 0 : 250}
            sx={{
              "& .MuiDrawer-paper": {
                width: "280px",
                backgroundColor: dt.cardBg,
                border: "none",
              },
              "& .MuiBackdrop-root": {
                backgroundColor: "rgba(0,0,0,0.5)",
                transition: backdropTransition,
              },
            }}
          >
            <MidasSidebar
              sidebarState="expanded"
              onToggle={handleCloseMobileDrawer}
              prefersReducedMotion={prefersReducedMotion}
            />
          </Drawer>
        </>
      )}

      {/* ── Chat Area -- fills remaining space ── */}
      <Box
        role="main"
        aria-label={translate("midas.aria.chatArea") || "Chat conversation area"}
        sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: "310px", overflow: "hidden" }}
      >
        <MidasChatArea prefersReducedMotion={prefersReducedMotion} />
      </Box>

      {/* ── Artifact Panel ── */}
      {/* Desktop: inline with drag handle */}
      {isDesktop && artifactPanelOpen && (
        <Box
          role="complementary"
          aria-label={translate("midas.aria.artifactPanel") || "Artifact viewer panel"}
          sx={{ display: "flex", height: "100%", flexShrink: 0 }}
        >
          <MidasDragHandle />
          <Suspense fallback={artifactFallback}>
            <MidasArtifactPanel prefersReducedMotion={prefersReducedMotion} />
          </Suspense>
        </Box>
      )}

      {/* Tablet: artifact as overlay from right */}
      {isTablet && artifactPanelOpen && (
        <>
          <Backdrop
            open={artifactPanelOpen}
            onClick={() => dispatch(closeArtifactPanel())}
            sx={{
              zIndex: 1200,
              backgroundColor: "rgba(0,0,0,0.3)",
              transition: backdropTransition,
            }}
          />
          <Box
            role="complementary"
            aria-label={translate("midas.aria.artifactPanel") || "Artifact viewer panel"}
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "min(480px, 65vw)",
              zIndex: 1201,
              boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
              animation: prefersReducedMotion
                ? "none"
                : "midasSlideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1) both",
            }}
          >
            <Suspense fallback={artifactFallback}>
              <MidasArtifactPanel prefersReducedMotion={prefersReducedMotion} />
            </Suspense>
          </Box>
        </>
      )}

      {/* Phone: artifact as bottom sheet */}
      {isPhone && artifactPanelOpen && (
        <MidasPhoneArtifactSheet prefersReducedMotion={prefersReducedMotion} />
      )}
    </Box>
  );
};

export default MidasPage;
